/**
 * RubyLingo — `WordGlyph`: vẽ "ô hình" của một từ KHÔNG có ảnh chụp (chữ cái / số).
 *
 * ⭐ DỮ LIỆU Ở `shared/word-glyphs.ts`, HÌNH Ở ĐÂY. Hai tệp tách nhau vì dữ liệu phải đọc được
 *   từ `scripts/validate-content.ts` (Node, không có DOM), còn tệp này là React + SVG.
 *
 * ⭐ VÌ SAO SVG NỘI TUYẾN, KHÔNG PHẢI TỆP `.png`/`.webp`:
 *   • 46 ô này là HÌNH DẠNG KÝ TỰ và SỐ LƯỢNG — vector vẽ ra là đúng bản chất, không cần sinh ảnh.
 *   • Không có request mạng nào ⇒ thẻ không bao giờ hiện ô vỡ khi mạng chậm, và không phải thêm
 *     46 tệp nhị phân vào repo.
 *   • Số chấm tròn SUY TỪ DỮ LIỆU (`count`), nên không thể "vẽ tay sai một chấm" như ảnh chụp.
 *
 * ⚠️⚠️ `width="1em" height="1em"` + `viewBox` VUÔNG — KHÔNG phải `width="100%"`:
 *   `.wi-fill` (`src/styles/word-icon.css`) đặt `font-size` theo BỀ RỘNG KHUNG (88cqw), và nhánh
 *   `<img>` sẵn có cũng là `h-[1em] w-[1em]`. Giữ đúng `1em` thì ô hình chảy vào CÙNG cơ chế đó,
 *   to bằng ảnh và bằng emoji ở mọi khung — không phải thêm một nhánh cỡ riêng để rồi lệch.
 *
 * ⚠️ CHỮ TRONG Ô HÌNH PHẢI LÀ CHỮ THƯỜNG (chữ cái) — xem khối chú thích đầu
 *   `shared/word-glyphs.ts`: thẻ chữ của cặp đó là chữ HOA, nên ô hình vẽ chữ HOA sẽ biến trò
 *   "ghép cặp" thành "tìm hai thẻ giống nhau".
 *
 * ⚠️ MÀU CHỈ LẤY TỪ TOKEN (`src/styles/tokens.css`), không hex lạ: ô hình đứng cạnh ảnh thật và
 *   chip của chủ đề, nên nó phải đổi màu cùng bảng màu chứ không phải một bảng riêng.
 */

import { useId } from 'react';

import type { WordGlyph as WordGlyphData } from '@shared/word-glyphs.js';

import { cn } from '../../lib/cn.js';

/**
 * Bảng màu theo LOẠI ô — dùng đúng bộ token của chủ đề tương ứng
 * (`--c-th-alphabet-*` cho chữ cái, `--c-th-numbers-1-20-*` cho số).
 *
 * ⭐ `ink` là bản CHỮ của tông màu (đo 4,61–5,41:1 trên mọi nền nó từng đứng — xem `tokens.css`).
 *   Dùng nó cho cả chữ số LẪN chấm tròn: chấm tròn ở đây mang NGHĨA (đếm được), không phải trang
 *   trí, nên nó phải đạt ngưỡng tương phản của thành phần mang nghĩa chứ không phải của hoạ tiết.
 */
const LETTER_PALETTE = {
  from: 'var(--c-th-alphabet-tint)',
  to: 'var(--c-th-alphabet-soft)',
  ink: 'var(--c-th-alphabet-ink)',
  ring: 'var(--c-th-alphabet)',
} as const;

const NUMBER_PALETTE = {
  from: 'var(--c-th-numbers-1-20-tint)',
  to: 'var(--c-th-numbers-1-20-soft)',
  ink: 'var(--c-th-numbers-1-20-ink)',
  ring: 'var(--c-th-numbers-1-20)',
} as const;

/** Chữ số nằm ở nửa trên, chấm tròn ở nửa dưới — mắt đọc "mấy" trước, rồi đếm để kiểm chứng. */
const DIGIT_Y = 22;
const DOTS_CENTER_Y = 68;

/**
 * Bố cục chấm tròn cho `count` chấm.
 *
 * ⚠️ 4 CỘT khi `count >= 5`, còn 1–4 thì MỘT HÀNG: 4 cột là số cột nhiều nhất mà vẫn đọc được
 *   "hàng chục + hàng đơn vị" (5 = 4 + 1, 10 = 4 + 4 + 2). Ít chấm mà cũng ép 4 cột thì hàng cuối
 *   chỉ có 1 chấm lẻ loi, trông như lỗi.
 *
 * ⚠️ Hàng cuối NGẮN HƠN được canh TRÁI (không canh giữa): bé đếm theo thứ tự trái→phải,
 *   trên→dưới; canh giữa hàng cuối làm mắt nhảy chỗ giữa chừng và dễ đếm sót.
 */
function dotLayout(count: number): { dots: Array<{ x: number; y: number }>; radius: number } {
  const cols = count <= 4 ? count : 4;
  const rows = Math.ceil(count / cols);
  const colPitch = 20;
  const rowPitch = rows >= 5 ? 11.5 : 15;
  const radius = rows === 1 ? 8 : rows === 2 ? 7 : rows === 3 ? 6.5 : rows === 4 ? 6 : 4.8;
  const startX = 50 - ((cols - 1) * colPitch) / 2;
  const startY = DOTS_CENTER_Y - ((rows - 1) * rowPitch) / 2;

  const dots: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < count; i += 1) {
    dots.push({
      x: startX + (i % cols) * colPitch,
      y: startY + Math.floor(i / cols) * rowPitch,
    });
  }
  return { dots, radius };
}

/** Chữ THƯỜNG cỡ lớn — cặp "A" (thẻ chữ) ↔ "a" (ô hình) dạy chữ hoa/chữ thường. */
function LetterBody({ letter, ink }: { letter: string; ink: string }) {
  return (
    <text
      x="50"
      y="52"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="64"
      fontWeight="700"
      fill={ink}
      style={{ fontFamily: 'var(--font-display)' }}
    >
      {letter}
    </text>
  );
}

/** Chữ số + ĐÚNG `count` chấm tròn — bài học về số lượng. */
function NumberBody({ count, ink }: { count: number; ink: string }) {
  const { dots, radius } = dotLayout(count);

  return (
    <>
      <text
        x="50"
        y={DIGIT_Y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="30"
        fontWeight="700"
        fill={ink}
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {count}
      </text>
      {dots.map((dot, index) => (
        <circle
          // Các chấm không có danh tính riêng — THỨ TỰ mới là nội dung (đếm 1, 2, 3…).
          key={index}
          cx={dot.x}
          cy={dot.y}
          r={radius}
          fill={ink}
        />
      ))}
    </>
  );
}

export interface WordGlyphProps {
  glyph: WordGlyphData;
  /** Class thêm — cỡ đã do lớp bọc `.wi-fill` quyết định (giống `WordIcon`). */
  className?: string;
}

export function WordGlyph({ glyph, className }: WordGlyphProps) {
  /**
   * `useId` cho `<linearGradient>`: mỗi ô là một `<svg>` riêng, và hai `<defs id>` trùng nhau
   * trong cùng một tài liệu là HTML không hợp lệ (trình duyệt lấy cái ĐẦU TIÊN — chạy được, nhưng
   * dựa vào hành vi không được bảo đảm). Id của React chứa dấu `:` nên phải lọc bỏ trước khi đem
   * vào `url(#…)`.
   */
  const gradientId = `wg-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const palette = glyph.kind === 'letter' ? LETTER_PALETTE : NUMBER_PALETTE;

  return (
    <svg
      viewBox="0 0 100 100"
      width="1em"
      height="1em"
      // Trang trí: nghĩa của từ đã được đọc bằng `aria-label` ở nơi gọi (thẻ ghi nhớ đọc TỪ TIẾNG
      // ANH, không đọc ký tự trong ô) — nên trình đọc màn hình không được đọc "a" ở đây.
      aria-hidden="true"
      focusable="false"
      className={cn('inline-block select-none align-[-0.12em]', className)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={palette.from} />
          <stop offset="100%" stopColor={palette.to} />
        </linearGradient>
      </defs>

      <rect
        x="2"
        y="2"
        width="96"
        height="96"
        rx="24"
        fill={`url(#${gradientId})`}
        stroke={palette.ring}
        strokeWidth="3"
      />

      {glyph.kind === 'letter' ? (
        <LetterBody letter={glyph.letter} ink={palette.ink} />
      ) : (
        <NumberBody count={glyph.count} ink={palette.ink} />
      )}
    </svg>
  );
}
